// Fetches the round-by-round chart after the tournament page has rendered. The
// series costs roughly 300 queries, so loading it inline would about double the
// time to first paint. Turbolinks, not Turbo.
//
// Once loaded it also sizes the panel to match the leaderboard beside it, and
// wires hover on the points.

const MATCH_HEIGHT_FROM = '(min-width: 1051px)'; // desk, where the leaderboard sits alongside

// Cards already wired, so the two init events on a first load cannot double up
// the listeners. A WeakSet rather than a data attribute: Turbolinks' cached copy
// of the page carries attributes over but not listeners, and must be rewired.
const wiredCards = new WeakSet();

// The chart is hidden on a phone while a tournament is still running
// (tournaments/show). It is not fetched while hidden -- ~300 queries for a chart
// nobody can see -- but is loaded if the page widens past the tab breakpoint
// (e.g. a phone turned to landscape) and the chart appears.
const REVEAL_FROM = window.matchMedia('(min-width: 701px)');
let revealWatched = false;

const TournamentProgression = {
  init() {
    const panel = document.querySelector('[data-progression]');
    if (!panel) return;
    if (panel.dataset.loaded === 'true') {
      // Restored from Turbolinks' cache (Back / Forward): the chart markup came
      // back, its listeners and observers did not. Wire it again; wire() is a
      // no-op while the first-load fetch is still in flight.
      TournamentProgression.wire(panel);
      return;
    }
    if (panel.offsetParent === null) {
      if (!revealWatched) {
        revealWatched = true;
        REVEAL_FROM.addEventListener('change', () => TournamentProgression.init());
      }
      return;
    }
    panel.dataset.loaded = 'true';

    fetch(panel.dataset.progressionUrl, {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin'
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        // An empty body means the tournament has no chartable rounds.
        if (html.trim() === '') {
          panel.remove();
          return;
        }
        panel.innerHTML = html;
        TournamentProgression.wire(panel);
      })
      .catch(() => {
        panel.innerHTML =
          '<div class="faso-card faso-caption">Could not load the round-by-round chart.</div>';
      });
  },

  wire(panel) {
    const card = panel.querySelector('[data-progression-panel]');
    const svg = panel.querySelector('[data-progression-svg]');
    const tip = panel.querySelector('[data-progression-tooltip]');
    if (!card || !svg || !tip || wiredCards.has(card)) return;
    wiredCards.add(card);

    const points = Array.from(svg.querySelectorAll('[data-point]'));
    const leaderboard = document.querySelector('[data-leaderboard]');

    // Everything hover needs is worked out once here, so moving the pointer does
    // no querying, no string building and no geometry reads.
    const meta = new Map();
    const byKey = new Map();
    points.forEach((point) => {
      const key = `${point.dataset.col}:${point.dataset.position}`;
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(point);
    });

    points.forEach((point) => {
      const group = byKey.get(`${point.dataset.col}:${point.dataset.position}`);
      meta.set(point, {
        lit: group.flatMap((p) =>
          Array.from(svg.querySelectorAll(`[data-line="${p.dataset.line}"]`))
        ),
        html:
          `<div class="text-gray-400">${point.dataset.label} &middot; ` +
          `<span class="text-gray-900 font-semibold">${point.dataset.rank}</span></div>` +
          group
            .map(
              (p) =>
                `<div class="flex items-center gap-2 pt-1"><span class="inline-block h-2 w-2 rounded-sm" ` +
                `style="background:${p.dataset.color}"></span>${p.dataset.golfer}</div>`
            )
            .join(''),
        x: 0,
        y: 0
      });
    });

    // Point centres relative to the card, cached. Recomputed only when the card
    // resizes, never while hovering.
    function measure() {
      const box = card.getBoundingClientRect();
      points.forEach((point) => {
        const dot = point.getBoundingClientRect();
        const m = meta.get(point);
        m.x = dot.left - box.left + dot.width / 2;
        m.y = dot.top - box.top;
      });
    }

    function syncHeight() {
      if (!leaderboard || !leaderboard.isConnected) return;
      if (!window.matchMedia(MATCH_HEIGHT_FROM).matches) {
        card.style.height = '';
        return;
      }
      // 0 means the page is being swapped out (Turbolinks navigation): keep the
      // last good height rather than writing 0 into the copy Back will restore.
      const height = Math.round(leaderboard.getBoundingClientRect().height);
      if (height > 0) card.style.height = `${height}px`;
    }

    let active = null;

    function clear() {
      if (!active) return;
      meta.get(active).lit.forEach((el) => el.classList.remove('is-lit'));
      svg.removeAttribute('data-dimmed');
      tip.classList.add('hidden');
      active = null;
    }

    function show(point) {
      if (point === active) return;
      if (active) meta.get(active).lit.forEach((el) => el.classList.remove('is-lit'));

      const m = meta.get(point);
      m.lit.forEach((el) => el.classList.add('is-lit'));
      svg.setAttribute('data-dimmed', '');

      tip.innerHTML = m.html;
      tip.style.left = `${m.x}px`;
      tip.style.top = `${m.y - 10}px`;
      tip.classList.remove('hidden');
      active = point;
    }

    // Track the pointer across the whole plot and snap to the nearest point,
    // the way the canvas race chart does. Requiring a hit on a 3px dot is what
    // made this feel unresponsive: most movement produced nothing at all.
    // Nearest column first, then nearest point within it, so a crowded column
    // still resolves to the row the pointer is actually on.
    let queued = false;
    let pending = null;

    function pick(clientX, clientY) {
      const box = card.getBoundingClientRect();
      const x = clientX - box.left;
      const y = clientY - box.top;

      let best = null;
      let bestColumn = Infinity;
      points.forEach((point) => {
        const dx = Math.abs(meta.get(point).x - x);
        if (dx < bestColumn - 0.5) bestColumn = dx;
      });

      let bestDy = Infinity;
      points.forEach((point) => {
        const m = meta.get(point);
        if (Math.abs(m.x - x) > bestColumn + 0.5) return;
        const dy = Math.abs(m.y - y);
        if (dy < bestDy) {
          bestDy = dy;
          best = point;
        }
      });

      return best;
    }

    svg.addEventListener('mousemove', (event) => {
      pending = event;
      if (queued) return;
      queued = true;
      // Coalesce to one lookup per frame; a mousemove can fire many times between paints.
      requestAnimationFrame(() => {
        queued = false;
        const point = pick(pending.clientX, pending.clientY);
        if (point) show(point);
      });
    });

    svg.addEventListener('mouseleave', clear);

    TournamentProgression.bindTabs(panel, clear);

    syncHeight();
    measure();
    if (window.ResizeObserver) {
      new ResizeObserver(() => {
        syncHeight();
        measure();
      }).observe(card);
      if (leaderboard) new ResizeObserver(syncHeight).observe(leaderboard);
    }
  },

  // Switches between the plot and the two framed race charts. A frame's src is
  // set the first time its tab is opened, so a tab nobody visits costs nothing.
  bindTabs(panel, clearHover) {
    const tabs = Array.from(panel.querySelectorAll('[data-prog-tab]'));
    const panes = Array.from(panel.querySelectorAll('[data-prog-pane]'));
    const caption = panel.querySelector('[data-prog-caption-out]');
    if (tabs.length < 2) return;

    // Selected styling comes from .faso-seg button[aria-selected="true"].
    function select(tab) {
      const key = tab.dataset.progTab;

      tabs.forEach((other) => {
        other.setAttribute('aria-selected', String(other === tab));
      });

      panes.forEach((pane) => pane.classList.toggle('hidden', pane.dataset.progPane !== key));

      if (caption) caption.innerHTML = tab.dataset.progCaption || '';

      // The hover tooltip belongs to the plot; it must not float over a frame.
      if (key !== 'plot') clearHover();

      const pane = panes.find((p) => p.dataset.progPane === key);
      if (pane && pane.tagName === 'IFRAME' && !pane.src && tab.dataset.progSrc) {
        pane.src = tab.dataset.progSrc;
      }
    }

    tabs.forEach((tab) => tab.addEventListener('click', () => select(tab)));
  }
};

document.addEventListener('DOMContentLoaded', TournamentProgression.init);
document.addEventListener('turbolinks:load', TournamentProgression.init);

export default TournamentProgression;
